/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * roles
 * -----
 * Role helpers for the application-user login response.
 *
 * /arcee/user/login/ returns roles as a list of objects scoped to the
 * application being logged into:
 *     roles: [{ id, name, label, permissions: [] }]
 *
 * Two things make a defensive reader necessary:
 *  1. /arcee/user/login/check does NOT return roles. SignInPage restores
 *     userData from that response on reload, so a roles-less userData is a
 *     normal state, not a bug — hence the separate ROLES_KEY copy written at
 *     login and read as a fallback.
 *  2. Older payloads (and some SSO paths) carry plain strings rather than
 *     objects, so both shapes are accepted.
 */

import { store, retrieve, remove } from './storage_helper';

export const ROLE_ORGANIZER_ADMIN = 'ORGANIZATION_ADMIN';

/**
 * Role names that open the organizer dashboard.
 *
 * Membership is an EXACT, case-sensitive comparison against the role name
 * exactly as the login response stored it — no lowercasing, no trimming, no
 * prefix matching. 'Organizer_Admin', 'ORGANIZER_ADMIN' and 'organizer' are
 * different roles and none of them opens this surface.
 *
 * `organizer_admin` is the name the AthloHive pack seeds
 * (arcee/arceeapp/management/commands/update_athlohive_pack.py). Application
 * 308 currently issues `ORGANIZATION_ADMIN` instead — add that string to this
 * list if those users should reach the organizer dashboard too.
 */
export const ORGANIZER_ROLES = [ROLE_ORGANIZER_ADMIN];

/** localStorage key holding the roles captured from the login response. */
export const ROLES_KEY = 'userRoles';

/** localStorage key holding a remembered organizer/athlete landing choice. */
export const ROLE_CHOICE_KEY = 'organizerRoleChoice';

export const ROLE_CHOICE_ORGANIZER = 'organizer';
export const ROLE_CHOICE_ATHLETE = 'athlete';

/** Normalise one role entry (object or bare string) to its name. */
function roleName(entry) {
  if (!entry) return null;
  if (typeof entry === 'string') return entry;
  return entry.name || entry.role || null;
}

/**
 * Persist the roles carried by a login response so they survive a reload,
 * where login/check cannot supply them.
 */
export function storeRoles(roles) {
  const names = Array.isArray(roles)
    ? roles.map(roleName).filter(Boolean)
    : [];
  store(ROLES_KEY, names);
  return names;
}

/** Every role name for the signed-in user, exactly as the API spelled them. */
export function getRoles(userData) {
  const data = userData || retrieve('userData');
  const fromUser = Array.isArray(data?.roles)
    ? data.roles.map(roleName).filter(Boolean)
    : [];
  const fromStore = Array.isArray(retrieve(ROLES_KEY)) ? retrieve(ROLES_KEY) : [];
  const all = fromUser.length ? fromUser : fromStore;
  return all.map((n) => String(n));
}

/**
 * True when the signed-in user holds the given role.
 *
 * Exact, case-sensitive match on the role's `name`. A role is an identifier the
 * backend controls, not user-entered text, so "Organizer_Admin" or
 * "organizer admin" are different roles — not sloppy spellings of this one —
 * and must not open the organizer surface.
 */
export function hasRole(name, userData) {
  if (!name) return false;
  return getRoles(userData).includes(name);
}

/**
 * True when the signed-in user can open the organizer dashboard.
 *
 * Compares each stored role name against ORGANIZER_ROLES with ===, so only a
 * byte-for-byte match of the name the API returned counts.
 */
export function isOrganizerAdmin(userData) {
  return getRoles(userData).some((name) => ORGANIZER_ROLES.includes(name));
}

export function clearRoleState() {
  remove(ROLES_KEY);
  // Clears a landing choice saved by the retired "Remember my choice" box.
  remove(ROLE_CHOICE_KEY);
}
